import { useEffect, useRef, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router";
import { postJson } from "../api";
import { useTexts, type Label } from "../texts";
import { AuthPage, Field, Notice, PasswordInput, Switch, describedBy, showResult } from "../ui/kit";

type LoginResult = {
  status: "signedIn" | "changePassword" | "invalid" | "error";
  next: string | null;
  code: string | null;
  messageKey: string | null;
  retryAfterSeconds: number | null;
  fields: { field: string; messageKey: string }[] | null;
};

/** Espera en segundos o minutos redondeados hacia arriba, solo si el texto tiene {time} (LoginFeedback.FormatMessage). */
export function formatWait(template: string, seconds: number | null, L: Label): string {
  if (!seconds || seconds <= 0 || !template.includes("{time}")) return template;
  const minutes = seconds >= 60;
  const count = minutes ? Math.ceil(seconds / 60) : seconds;
  const unit = L(
    minutes ? (count === 1 ? "LOGIN_MINUTE" : "LOGIN_MINUTES") : count === 1 ? "LOGIN_SECOND" : "LOGIN_SECONDS",
  );
  return template.replace("{time}", `${count} ${unit}`);
}

/** Aviso que trae la dirección: el formulario caducó, la sesión terminó o se cerró, o la contraseña ya se cambió. */
function arrivalNotice(params: URLSearchParams): { key: string; tone: "warning" | "done" } | null {
  if (params.get("changed") === "1") return { key: "PWD_CHANGED", tone: "done" };
  switch (params.get("error")) {
    case "expired":
      return { key: "LOGIN_SESSION_EXPIRED", tone: "warning" };
    case "session-ended":
      return { key: "LOGIN_SESSION_ENDED", tone: "warning" };
    case "closed":
      return { key: "LOGIN_SESSION_CLOSED", tone: "done" };
    default:
      return null;
  }
}

/**
 * Ingreso del Platform Admin (Login.razor) con las piezas del kit: el servidor valida con Identity y decide el paso
 * siguiente (la pantalla pedida o el cambio obligatorio de contraseña). Tras un error la contraseña queda vacía; el correo
 * y el interruptor se conservan; cada error va bajo su campo y un rechazo general va arriba del formulario.
 */
export function LoginPage() {
  const { L } = useTexts();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [keep, setKeep] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [attempt, setAttempt] = useState(0);
  const password = useRef<HTMLInputElement>(null);
  const busy = useRef(false);
  const arrival = arrivalNotice(params);
  useEffect(() => {
    document.title = `${L("LOGIN_TITLE")} - Bheli Admin`;
  }, [L]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy.current) return;
    busy.current = true;
    setSending(true);
    let leaving = false;
    try {
      const reply = await postJson<LoginResult>("/api/admin/session/login", {
        email,
        password: password.current?.value ?? "",
        keepSignedIn: keep,
        returnUrl: params.get("ReturnUrl"),
      }).catch(() => null);
      const result = reply?.body;
      if (result?.next && (result.status === "signedIn" || result.status === "changePassword")) {
        // Hasta que cargue la pantalla siguiente el botón sigue cargando y no acepta otro envío.
        leaving = true;
        window.location.assign(result.next);
        return;
      }
      const issues = result?.fields ?? [];
      if (password.current) password.current.value = "";
      showResult(() => {
        setFields(Object.fromEntries(issues.map((issue) => [issue.field, L(issue.messageKey)])));
        setError(
          issues.length > 0
            ? null
            : formatWait(L(result?.messageKey ?? "LOGIN_SERVICE_UNAVAILABLE"), result?.retryAfterSeconds ?? null, L),
        );
        setAttempt((value) => value + 1);
      }, issues.length > 0);
    } finally {
      if (!leaving) {
        busy.current = false;
        setSending(false);
      }
    }
  };

  return (
    <AuthPage title={L("LOGIN_TITLE")} product={L("ADM_PRODUCT")}>
      {error ? (
        <Notice key={attempt} tone="error">
          {error}
        </Notice>
      ) : arrival ? (
        <Notice tone={arrival.tone}>{L(arrival.key)}</Notice>
      ) : null}
      <form method="post" noValidate onSubmit={(event) => void submit(event)}>
        <Field id="email" label={L("LOGIN_EMAIL")} error={fields.email}>
          <input
            id="email"
            name="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            type="email"
            autoComplete="username"
            aria-invalid={fields.email ? "true" : undefined}
            aria-describedby={describedBy("email", fields.email)}
          />
        </Field>
        <Field id="password" label={L("LOGIN_PASSWORD")} error={fields.password}>
          <PasswordInput
            id="password"
            name="password"
            autoComplete="current-password"
            inputRef={password}
            error={fields.password}
            showLabel={L("LOGIN_SHOW_PASSWORD")}
            hideLabel={L("LOGIN_HIDE_PASSWORD")}
          />
        </Field>
        <Switch
          title={L("LOGIN_KEEP_SIGNED_IN")}
          line={L(keep ? "LOGIN_KEEP_SIGNED_IN_ON" : "LOGIN_KEEP_SIGNED_IN_OFF")}
          checked={keep}
          onChange={setKeep}
        />
        <div className="tm-form-actions auth-actions" data-part="acciones">
          <button type="submit" className="tm-btn primary" aria-busy={sending ? "true" : undefined}>
            {L("LOGIN_SUBMIT")}
          </button>
        </div>
      </form>
    </AuthPage>
  );
}
